import { isPlatformServer } from '@angular/common';
import {Injectable, PLATFORM_ID, inject} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { APP_LOCATION } from '@core/config/app-location';
import { AppSurfaceService } from '@core/config/app-surface.service';
import { UserQuery } from '@core/store/user/user.query';
import { UserService } from '@core/store/user/user.service';
import { environment } from '@environments/environment';
import { RESPONSE } from '../../../express.tokens';
import { CookieService } from '@shared/services/cookie.service';
import { Observable, EMPTY, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from '@zitemedia/zitadel-sso-frontend';
import {AmApiKeyService} from '@core/services/am-api-key.service';
import { POST_LOGIN_RETURN_URL_KEY } from '@core/constants/auth-storage-keys';

@Injectable({ providedIn: 'root' })
export class AuthGuard  {
  private _router = inject(Router);
  private _userQuery = inject(UserQuery);
  private _userService = inject(UserService);
  private _cookieService = inject(CookieService);
  private _res = inject(RESPONSE, { optional: true })!;
  private _platformId = inject(PLATFORM_ID);
  private _authService = inject(AuthService);
  private readonly ApiKeyService = inject(AmApiKeyService);
  private _location = inject(APP_LOCATION);
  private _surface = inject(AppSurfaceService);

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor(private amKey: AmApiKeyService) {
  }

  somethingWentWrong(returnUrl: string) {
    if (isPlatformServer(this._platformId)) {
      this._res.status(403);
    }

    // Set error message for authentication issues
    this._authService.setAuthServiceError(true);
    this._authService.setAuthErrorMessage(
      'Authentication failed. Please try to log in again or contact an administrator.'
    );
    this.ApiKeyService.disable()
    this.sendToLogin(returnUrl);
  }

  /**
   * Remembers the deep-linked path so the post-login flow
   * (login-wrapper.component.ts) can send the user back to it instead of
   * the home page. Skipped for /login itself to avoid a redirect loop.
   */
  private storeReturnUrl(url?: string): void {
    if (isPlatformServer(this._platformId) || !url || url.startsWith('/login')) {
      return;
    }
    sessionStorage.setItem(POST_LOGIN_RETURN_URL_KEY, url);
  }

  /**
   * Send an unauthenticated visitor to the sign-in page.
   *
   * On the estimator host that is an in-app navigation, and the deep-linked
   * path is stashed in sessionStorage as before. On the briefings host the
   * sign-in page is on a different origin -- ZITADEL_REDIRECT_URI is pinned to
   * the estimator host and stays there -- so sessionStorage is no use: it is
   * per-origin, and a value written here is invisible after the browser lands
   * there. The return address travels in the query string instead, and
   * LoginWrapperComponent checks it against this environment's two origins
   * before honouring it.
   */
  sendToLogin(url: string): void {
    if (this._surface.current === 'estimator') {
      this.storeReturnUrl(url);
      this._router.navigate(['login']);
      return;
    }

    const back = `${this._location?.origin ?? ''}${url}`;
    const login = this._surface.urlFor(
      'estimator',
      `/login?next=${encodeURIComponent(back)}`,
    );
    this._location?.replace(login);
  }

  canActivate(route: ActivatedRouteSnapshot,
              state: RouterStateSnapshot): Observable<boolean> {
    if (isPlatformServer(this._platformId)) {
      return of(false);
    }
    const token = this._cookieService.getCookie(environment.authCookieName);
    const apiToken = this.amKey.isEnabled() ? this.amKey.getKey() : null;

    return this._userService.loadUser()
      .pipe(
        map((user: any) => {
          // Persist the loaded user in the store so components using userQuery.getUser$ have data after refresh
          this._userService.setUser(user);

          if (!token && !apiToken) {
            // This is the specific case mentioned in the issue description
            // where the callback was successful but the cookie wasn't set
            this._authService.setAuthServiceError(true);
            this._authService.setAuthErrorMessage(
              'Authentication failed. The server did not set the required authentication token.' +
              ' Please try to log in again or contact an administrator.'
            );
            this.sendToLogin(state.url);
          }
          return !!token || !!apiToken;
        }),
        catchError((err) => {
          console.error(err);
          this.somethingWentWrong(state.url);

          return EMPTY;
        })
      );
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(childRoute, state);
  }
}
